import { AxiosError } from "axios";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
  INVERT_COLOR_THRESHOLD,
  mappedTrendFilterType,
  TrendsFilterType,
} from "../constants/constants";
import { getTrendFiltersForGraphs } from "./trendSidebarGraphUtils/graphHelperUtils";

export const stringShortener = (str: string, maxLength: number) => {
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "...";
  }
  return str;
};

export const validateEmail = (email: string) => {
  // Regular expression for email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const capitalizeFirstLetter = (str: string) => {
  return str
    .trimStart() // Remove leading spaces
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
export const capitalizeFirstLetterRemainingAsItIs = (str: string) => {
  const defaultVal = str;

  const res = str
    .trimStart() // Remove leading spaces
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return res;
};

export const copyImageToClipboard = async (
  imageUrl: string,
  sourceTag: string,
  tagWidthPercentage = 10,
  tagHeightPercentage = 5,
  offsetXPercentage = 1
) => {
  try {
    await document.fonts.ready;
    const warmUpCanvas = document.createElement("canvas");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const warmUpCtx: any = warmUpCanvas.getContext("2d");
    warmUpCtx.font = `10px sans-serif`;
    warmUpCtx.fillText("Test", 0, 0);

    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const img = document.createElement("img");
    img.src = URL.createObjectURL(blob);

    img.onload = async () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      ctx.drawImage(img, 0, 0);

      const tagWidth = (canvas.width * tagWidthPercentage) / 100;
      const tagHeight = (canvas.height * tagHeightPercentage) / 100;
      const fontSize = tagHeight * 0.6;
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";

      const paddingX = 6;
      const paddingRight = 12;
      const paddingY = 8;

      const textWidth = ctx.measureText(sourceTag).width;

      const adjustedTagWidth = Math.max(
        tagWidth,
        textWidth + paddingX * 3 + paddingRight
      );
      const offsetX = (canvas.width * offsetXPercentage) / 100;

      const rectX = canvas.width - adjustedTagWidth + offsetX;
      const rectY = 0;

      const cornerRadius = 20;
      ctx.fillStyle = "#6B72807F";
      ctx.beginPath();
      ctx.moveTo(rectX + adjustedTagWidth, rectY);
      ctx.lineTo(rectX + adjustedTagWidth, rectY + tagHeight);
      ctx.lineTo(rectX + cornerRadius, rectY + tagHeight);
      ctx.quadraticCurveTo(
        rectX,
        rectY + tagHeight,
        rectX,
        rectY + tagHeight - cornerRadius
      );
      ctx.lineTo(rectX, rectY);
      ctx.lineTo(rectX + adjustedTagWidth, rectY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "white";
      ctx.fillText(
        sourceTag,
        canvas.width - paddingX - paddingRight + offsetX,
        rectY + tagHeight / 2
      );

      canvas.toBlob(async taggedBlob => {
        if (taggedBlob) {
          await navigator.clipboard.write([
            new ClipboardItem({
              [taggedBlob.type]: taggedBlob,
            }),
          ]);
          toast.success("Image with source tag copied to clipboard!", {
            hideProgressBar: true,
          });
        } else {
          throw new Error("Failed to create a blob from the canvas");
        }
      }, "image/png");
    };
  } catch (error) {
    toast.error("Failed to copy image to clipboard.", {
      hideProgressBar: true,
    });
  }
};

export const formatDate = (inputDate: string | undefined) => {
  if (inputDate === undefined) {
    return "Unavaiable";
  } else {
    const date = new Date(inputDate);

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }
};
export const formatDateTimeToReadable = (isoString: string): string => {
  const date = new Date(isoString);
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours() % 12 || 12; // Convert to 12-hour format
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const ampm = date.getUTCHours() >= 12 ? "PM" : "AM";

  return `${day} ${month} ${year}  ,  ${hours}:${minutes} ${ampm}`;
};

export const formatDateWithTime = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();
  const hours = date.getHours() % 12 || 12; // Convert to 12-hour format
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  return `${day}-${month}-${year} , ${hours}:${minutes} ${ampm}`;
};
export const handleError = (error: unknown) => {
  if ((error as AxiosError).response?.status === 400) {
    return;
  } else if (error instanceof AxiosError) {
    toast.error(error.response?.data?.message || "Error inviting user", {
      hideProgressBar: true,
    });
  } else if (error instanceof Error) {
    toast.error(error.message || "Unknown error occurred", {
      hideProgressBar: true,
    });
  } else {
    toast.error("An unexpected error occurred", {
      hideProgressBar: true,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFileName = (file: any) => {
  const fileExtension = file.name.split(".").pop();
  const fileName = `${uuidv4()}.${fileExtension}`;

  return fileName;
};

export const getKeyForS3DesignerPage = (fileName: string) => {
  return `embeddings/searchEmbeddings/${fileName}`;
};
export const getKeyForS3GenerativeFeature = (fileName: string) => {
  return `generative-features/searchEmbeddings/${fileName}`;
};

export const getKeyForS3PdfGeneration = (userId: string, timeStamp: number) => {
  return `pdf-generation/${userId}/${timeStamp}/graphData.json`;
};
export const compressImage = async (file: File): Promise<File> => {
  const options = {
    maxSizeKB: 300,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };

  try {
    return await imageCompression(file, options);
  } catch (error) {
    console.error("Error compressing image:", error);
    return file;
  }
};

export const convertMarkdownToText = (markdown: string) => {
  return markdown.replace(/(?:__|[*#])|\[(.*?)\]\(.*?\)/gm, "$1");
};

export const getImageLink = (url: string) => {
  if (!url) return url;

  const cloudFrontUrl = (process.env.REACT_APP_CLOUD_FRONT_URL ?? "").trim();

  // Uploads now store CloudFront (.net) URLs — use them as-is
  if (
    (cloudFrontUrl && url.startsWith(cloudFrontUrl)) ||
    url.includes("cloudfront.net/")
  ) {
    return url;
  }

  const comIndex = url.indexOf(".com/");
  if (comIndex === -1) return url;

  // CDN is best-effort — keep the original S3/http URL when env is unset.
  if (!cloudFrontUrl) return url;

  // Rewrite legacy S3 (.com) URLs to CloudFront
  const base = cloudFrontUrl.replace(/\/$/, "");
  return `${base}/${url.substring(comIndex + 5)}`;
};

export const isMeaningfulPostId = (
  postId?: string | number | null
): boolean => {
  if (postId === undefined || postId === null) return false;
  const value = String(postId).trim();
  return value !== "" && value !== "undefined" && value !== "null";
};

/** Instagram post id, or board content `_id` for uploaded images. */
export const resolveTicketPostId = (
  postId?: string | number | null,
  contentId?: string | null
): string => {
  if (isMeaningfulPostId(postId)) return String(postId).trim();
  if (isMeaningfulPostId(contentId)) return String(contentId).trim();
  return "";
};

export function invertColor(hex: string | undefined): string {
  if (!hex) return "#000000";
  let cleanedHex = hex.startsWith("#") ? hex.slice(1) : hex;
  if (cleanedHex.length === 3) {
    cleanedHex = cleanedHex
      .split("")
      .map(char => char + char)
      .join("");
  }
  if (cleanedHex.length !== 6) return "#000000";
  const r = parseInt(cleanedHex.slice(0, 2), 16);
  const g = parseInt(cleanedHex.slice(2, 4), 16);
  const b = parseInt(cleanedHex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > INVERT_COLOR_THRESHOLD ? "#000000" : "#FFFFFF";
}
export const areArraysEqual = (arr1: string[], arr2: string[]) => {
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
      return false;
    }
  }
  return true;
};

export const capitalizeFirstLetterAndRemainingSmallForEachWord = (
  str: string | undefined
) => {
  if (!str || !str.trim()) return "Unknown";

  return str
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const areArraysDifferentHavingDifferentOrder = (
  arr1: string[],
  arr2: string[]
) => {
  if (arr1.length !== arr2.length) return true;

  const set = new Set(arr1);
  for (const item of arr2) {
    if (!set.has(item)) return true;
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeKeysFromObject = (obj: any, keys: string[]) => {
  keys.forEach(key => {
    delete obj[key];
  });
};

export const fetchImage = (source: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image();
    img.src = source;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

export const getFallbackImageForNewsFeed = async (
  source: string,
  url: string,
  image: string | undefined,
  fromBoard?: boolean
): Promise<string> => {
  //Check if image available
  if (image) {
    const decodedImage = decodeURI(image);
    const imageLink = fromBoard ? decodedImage : getImageLink(decodedImage);
    const isImageLoaded = await fetchImage(imageLink);
    if (isImageLoaded) {
      return encodeURI(imageLink);
    }
  }

  //Check if favicon available or not
  const isFaviconAvailable = await fetchImage(
    `${new URL(url).origin}/favicon.ico`
  );
  if (isFaviconAvailable) {
    return `${new URL(url).origin}/favicon.ico`;
  }

  //If favicon not available
  const firstWord = source.split(" ")[0];
  const canvas = document.createElement("canvas");
  canvas.width = 70;
  canvas.height = 70;
  const context = canvas.getContext("2d");
  if (context) {
    context.fillStyle = "#000000";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#FFFFFF";
    context.font = "bold 20px Arial";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(firstWord, canvas.width / 2, canvas.height / 2);
    const imageUrl = canvas.toDataURL();
    return encodeURI(imageUrl);
  }

  return "https://www.bing.com/th?id=OVFT.94fhU2WuyPqa4wDZIRambS&pid=News";
};

export const getKeyFromValueForTrendQueryType = (query_type: string) => {
  return Object.entries(mappedTrendFilterType).find(
    ([key, value]) => value === query_type
  )![0] as TrendsFilterType;
};

export function formatNumber(num: number): string {
  if (num >= 1_000_000_000)
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return num.toString();
}

export function getDisplaySource(
  scraping_source_id: number | null | undefined,
  source: string
): string {
  const prefix =
    scraping_source_id === 1 || scraping_source_id === null ? "IG/" : "";
  return `${prefix}${source}`;
}

//eslint-disable-next-line
export const validateField = (value: any, message: string): boolean => {
  if (!value) {
    toast.warn(message, { hideProgressBar: true });
    return false;
  }
  return true;
};
